/**
 * Iter Feb2026 · BiometricUploadTab
 *
 * XLSX upload for biometric device punches + a compact discrepancy
 * viewer that lets HR compare app vs device check-in / out for any
 * date.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtHM(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function BiometricUploadTab({ wsId }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [markers, setMarkers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);

  const load = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/attendance/check-in-markers`, { params: { date } })
       .then((r) => setMarkers(r.data?.markers || []))
       .catch(() => setMarkers([]));
  };
  useEffect(load, [wsId, date]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setLastUpload(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/workspaces/${wsId}/attendance/biometric-upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setLastUpload(r.data);
      toast.success(`Imported ${r.data.inserted} punches · ${r.data.duplicates_skipped} duplicates skipped`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const flagged = markers.filter((m) => m.delta_flag === "red").length;

  return (
    <div className="space-y-4">
      <div className="border border-border p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Upload biometric device data</div>
          <div className="text-xs text-muted-foreground mt-1">
            XLSX with headers <code>emp_id</code> / <code>biometric_id</code>,
            <code> timestamp</code>, optional <code>punch_type</code> (in / out).
            Dedupes on (emp_id, timestamp) so re-uploads are safe.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 h-9 px-3 border border-border bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90 rounded-none text-xs"
               data-testid="biometric-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : "Choose XLSX"}
          <input type="file" accept=".xlsx" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
      </div>

      {lastUpload && (
        <div className="border border-border p-3 text-xs bg-muted/30" data-testid="biometric-upload-result">
          <div className="font-semibold mb-1">Last upload</div>
          <div>Inserted: <strong>{lastUpload.inserted}</strong> · Duplicates skipped: <strong>{lastUpload.duplicates_skipped}</strong>
            {lastUpload.unresolved_count > 0 && (
              <span className="ml-2 text-amber-700">· Unresolved: <strong>{lastUpload.unresolved_count}</strong></span>
            )}
            {lastUpload.invalid_count > 0 && (
              <span className="ml-2 text-rose-700">· Invalid timestamps: <strong>{lastUpload.invalid_count}</strong></span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <label className="text-xs font-medium">Discrepancy report for</label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
               className="h-8 w-40 rounded-none" data-testid="biometric-discrepancy-date" />
        {flagged > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 text-[11px] border border-rose-500/60 bg-rose-500/10 text-rose-700 px-2 py-0.5">
            <AlertTriangle className="w-3 h-3" /> {flagged} flagged &gt; 15m
          </span>
        )}
      </div>

      <div className="border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Employee</th>
              <th className="px-3 py-2 font-semibold text-blue-700">App In</th>
              <th className="px-3 py-2 font-semibold text-purple-700">Device In</th>
              <th className="px-3 py-2 font-semibold">Δ In</th>
              <th className="px-3 py-2 font-semibold text-blue-700">App Out</th>
              <th className="px-3 py-2 font-semibold text-purple-700">Device Out</th>
              <th className="px-3 py-2 font-semibold">Δ Out</th>
              <th className="px-3 py-2 font-semibold">Flag</th>
            </tr>
          </thead>
          <tbody>
            {markers.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">No punches on this date.</td></tr>
            )}
            {markers.filter((m) => m.app_in || m.device_in || m.app_out || m.device_out).map((m) => {
              const flagCls = m.delta_flag === "red" ? "bg-rose-100 text-rose-800"
                : m.delta_flag === "yellow" ? "bg-amber-100 text-amber-800"
                : "bg-emerald-100 text-emerald-800";
              return (
                <tr key={m.employee_id} className="border-t border-border">
                  <td className="px-3 py-2">{m.name || m.emp_id || m.user_id}</td>
                  <td className="px-3 py-2 font-mono text-blue-700">{fmtHM(m.app_in)}</td>
                  <td className="px-3 py-2 font-mono text-purple-700">{fmtHM(m.device_in)}</td>
                  <td className="px-3 py-2 font-mono">{m.discrepancy_in_minutes != null ? `${m.discrepancy_in_minutes}m` : "—"}</td>
                  <td className="px-3 py-2 font-mono text-blue-700">{fmtHM(m.app_out)}</td>
                  <td className="px-3 py-2 font-mono text-purple-700">{fmtHM(m.device_out)}</td>
                  <td className="px-3 py-2 font-mono">{m.discrepancy_out_minutes != null ? `${m.discrepancy_out_minutes}m` : "—"}</td>
                  <td className="px-3 py-2"><span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${flagCls}`}>{m.delta_flag}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
